import { isSupabaseConfigured, SUPABASE_ANON_KEY, SUPABASE_URL } from "../config/supabasePublic";

const EDGE_SLUG = "talo-self-register";

function functionsUrl() {
  return `${String(SUPABASE_URL).replace(/\/$/, "")}/functions/v1/${EDGE_SLUG}`;
}

function anonHeaders() {
  return {
    "Content-Type": "application/json",
    apikey: SUPABASE_ANON_KEY,
    Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
  };
}

/**
 * Public Turnstile site key (safe to expose in the browser).
 * Loaded from Edge Function secret TURNSTILE_SITE_KEY.
 * @returns {Promise<{ ok: true, siteKey: string, configured: boolean } | { ok: false, error: string }>}
 */
export async function fetchTurnstileSiteKey() {
  if (!isSupabaseConfigured()) {
    return { ok: false, error: "Configuration serveur manquante." };
  }
  try {
    const res = await fetch(functionsUrl(), {
      method: "POST",
      headers: anonHeaders(),
      body: JSON.stringify({ op: "captcha.config" }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const msg =
        (typeof body.error === "string" && body.error) || `Erreur ${res.status}`;
      return { ok: false, error: msg };
    }
    const siteKey = String(body.siteKey ?? "").trim();
    return {
      ok: true,
      siteKey,
      configured: Boolean(body.configured) || siteKey.length > 10,
    };
  } catch (e) {
    return { ok: false, error: String(e?.message ?? e) };
  }
}

/**
 * @param {string} adminLogin
 * @returns {Promise<{ ok: true, available: boolean, invalid?: boolean } | { ok: false, error: string }>}
 */
export async function checkAdminLoginAvailable(adminLogin) {
  if (!isSupabaseConfigured()) {
    return { ok: false, error: "Configuration serveur manquante." };
  }
  try {
    const res = await fetch(functionsUrl(), {
      method: "POST",
      headers: anonHeaders(),
      body: JSON.stringify({
        op: "login.check",
        adminLogin: String(adminLogin ?? "").trim().toLowerCase(),
      }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      const msg =
        (typeof body.error === "string" && body.error) || `Erreur ${res.status}`;
      return { ok: false, error: msg };
    }
    return {
      ok: true,
      available: Boolean(body.available),
      invalid: Boolean(body.invalid),
    };
  } catch (e) {
    return { ok: false, error: String(e?.message ?? e) };
  }
}

/**
 * @param {{
 *   businessName: string,
 *   adminEmail: string,
 *   adminPassword: string,
 *   adminLogin?: string,
 *   adminUsername?: string,
 *   categoryName?: string,
 *   countryCode?: string,
 *   locationAddress?: string,
 *   requestedPlan?: string,
 *   lang?: string,
 *   website?: string,
 *   turnstileToken?: string,
 * }} payload
 * @returns {Promise<{ ok: true, pending?: boolean, emailSent?: boolean, adminLogin?: string } | { ok: false, error: string }>}
 */
export async function selfRegisterBusiness(payload) {
  if (!isSupabaseConfigured()) {
    return { ok: false, error: "Configuration serveur manquante." };
  }

  try {
    const res = await fetch(functionsUrl(), {
      method: "POST",
      headers: anonHeaders(),
      body: JSON.stringify({
        businessName: payload.businessName,
        adminEmail: payload.adminEmail,
        adminPassword: payload.adminPassword,
        adminLogin: payload.adminLogin || payload.adminUsername || "",
        adminUsername: payload.adminLogin || payload.adminUsername || "",
        categoryName: payload.categoryName ?? "",
        countryCode: payload.countryCode ?? "CD",
        locationAddress: String(payload.locationAddress ?? "").trim(),
        requestedPlan: payload.requestedPlan ?? "trial",
        lang: payload.lang ?? "fr",
        website: payload.website ?? "",
        turnstileToken: payload.turnstileToken ?? "",
      }),
    });

    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      if (res.status === 404) {
        return {
          ok: false,
          error:
            "Service d’inscription indisponible. Déployez la fonction talo-self-register.",
        };
      }
      const msg =
        (typeof body.error === "string" && body.error) ||
        (typeof body.message === "string" && body.message) ||
        (typeof body.msg === "string" && body.msg) ||
        `Erreur ${res.status}`;
      return { ok: false, error: msg };
    }
    if (!body.ok) {
      return {
        ok: false,
        error: typeof body.error === "string" ? body.error : "Inscription impossible.",
      };
    }
    return {
      ok: true,
      pending: body.pending !== false,
      businessId: body.businessId,
      emailSent: Boolean(body.emailSent),
      adminLogin: body.adminLogin,
    };
  } catch (e) {
    return { ok: false, error: String(e?.message ?? e) };
  }
}

/**
 * @param {string} token
 * @returns {Promise<{ ok: true, activated?: boolean, adminLogin?: string, emailSent?: boolean } | { ok: false, error: string }>}
 */
export async function verifySelfRegisterEmail(token) {
  if (!isSupabaseConfigured()) {
    return { ok: false, error: "Configuration serveur manquante." };
  }
  const t = String(token ?? "").trim();
  if (!t) {
    return { ok: false, error: "Lien de confirmation invalide." };
  }
  try {
    const res = await fetch(functionsUrl(), {
      method: "POST",
      headers: anonHeaders(),
      body: JSON.stringify({
        op: "email.verify",
        token: t,
      }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || !body.ok) {
      const msg =
        (typeof body.error === "string" && body.error) ||
        `Erreur ${res.status}`;
      return { ok: false, error: msg };
    }
    return {
      ok: true,
      activated: Boolean(body.activated),
      adminLogin: body.adminLogin,
      emailSent: Boolean(body.emailSent),
    };
  } catch (e) {
    return { ok: false, error: String(e?.message ?? e) };
  }
}

/** Client-side password policy (must match edge validateStrongPassword). */
export function validateSignupPassword(password) {
  const p = String(password ?? "");
  if (p.length < 8) {
    return { ok: false, code: "short" };
  }
  if (p.length > 128) {
    return { ok: false, code: "long" };
  }
  if (!/[A-Za-z]/.test(p) || !/[0-9]/.test(p)) {
    return { ok: false, code: "weak" };
  }
  return { ok: true };
}
